import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Megaphone, LifeBuoy, Bug, Lightbulb, MessageSquare, Image as ImageIcon,
  Loader2, Send, CheckCheck, X, Plus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { key: "support", label: "Message admin", icon: MessageSquare },
  { key: "bug", label: "Report a bug", icon: Bug },
  { key: "feature", label: "Feature idea", icon: Lightbulb },
];

export default function HostHelpUpdatesPanel({ eventId, user }) {
  const [updates, setUpdates] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("support");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [feedR, ticketsR] = await Promise.all([
        fetch(`${API}/updates/feed`, { headers: headers() }),
        fetch(`${API}/host/support/my-tickets`, { headers: headers() }),
      ]);
      if (feedR.ok) { const d = await feedR.json(); setUpdates(d.updates || d.feed || []); }
      if (ticketsR.ok) { const d = await ticketsR.json(); setMyTickets(d.tickets || []); }
    } catch (e) { console.warn(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const markRead = async (update_id) => {
    try {
      await fetch(`${API}/updates/${update_id}/read`, { method: "POST", headers: headers() });
      reload();
    } catch {}
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/support/ticket/upload`, { method: "POST", headers: headers(), body: fd });
      if (r.ok) { const d = await r.json(); setAttachments(a => [...a, d.image_url]); toast.success("Image attached"); }
      else toast.error("Upload failed");
    } catch { toast.error("Network error"); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!subject.trim() || !description.trim()) { toast.error("Subject and description required"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/support/ticket`, {
        method: "POST", headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user?.user_id, type, subject, description,
          email: user?.email, url: window.location.href,
          attachment_urls: attachments, event_id: eventId,
        })
      });
      if (r.ok) {
        toast.success("Sent. Admin will respond soon.");
        setSubject(""); setDescription(""); setAttachments([]);
        reload();
      } else toast.error("Submission failed");
    } catch { toast.error("Network error"); } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="help-updates-panel">
      <div>
        <h3 className="text-lg font-semibold">Help & Updates</h3>
        <p className="text-sm text-gray-500">Platform announcements, message admin, report bugs, and share feature ideas. Attach images if helpful.</p>
      </div>

      {/* Platform Updates */}
      <Card data-testid="platform-updates-card">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-purple-600" />
            <h4 className="font-semibold text-sm">Latest updates</h4>
          </div>
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-purple-500 mx-auto" /> : updates.length === 0 ? (
            <p className="text-xs text-gray-400 italic py-2">No updates yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {updates.slice(0, 10).map((u) => (
                <div key={u.update_id || u._id} className="p-2.5 rounded-lg border border-gray-100 bg-gray-50/50 flex items-start gap-2" data-testid={`update-${u.update_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">{u.title}</p>
                      {u.category && <Badge variant="outline" className="text-[10px]">{u.category}</Badge>}
                      {u.read ? <Badge variant="secondary" className="text-[10px]">Read</Badge> : <Badge className="text-[10px] bg-purple-100 text-purple-700 hover:bg-purple-100">New</Badge>}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{u.description || u.body}</p>
                  </div>
                  {!u.read && (
                    <Button size="sm" variant="ghost" onClick={() => markRead(u.update_id)}>
                      <CheckCheck className="w-3 h-3 text-purple-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit a ticket */}
      <Card data-testid="submit-ticket-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <LifeBuoy className="w-4 h-4 text-purple-600" />
            <h4 className="font-semibold text-sm">Reach out</h4>
          </div>

          <div className="flex gap-1.5 flex-wrap">
            {TYPES.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setType(t.key)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${type === t.key ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-200 hover:border-purple-300"}`} data-testid={`ticket-type-${t.key}`}>
                  <Icon className="w-3 h-3" /> {t.label}
                </button>
              );
            })}
          </div>

          <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ticket-subject" />
          <textarea placeholder="Tell us what's going on..." value={description} onChange={(e) => setDescription(e.target.value)} className="w-full text-sm border rounded px-3 py-2 min-h-[80px]" data-testid="ticket-description" />

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {attachments.map((url, i) => (
                <div key={i} className="relative w-16 h-16 rounded border overflow-hidden bg-gray-50">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setAttachments(a => a.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <label className="text-xs text-purple-700 cursor-pointer inline-flex items-center gap-1 hover:bg-purple-50 px-2 py-1 rounded" data-testid="ticket-attach-btn">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />} Attach image
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadImage(e.target.files[0]); e.target.value = ""; }} />
            </label>
            <Button size="sm" onClick={submit} disabled={submitting || !subject.trim() || !description.trim()} className="bg-purple-600 hover:bg-purple-700" data-testid="ticket-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Send</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* My tickets */}
      {myTickets.length > 0 && (
        <Card data-testid="my-tickets-card">
          <CardContent className="p-4 space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-purple-600" /> Your past messages</h4>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {myTickets.map((t) => (
                <div key={t.ticket_id} className="p-2.5 rounded border bg-gray-50/50" data-testid={`my-ticket-${t.ticket_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate">{t.subject}</p>
                    <Badge variant={t.status === "closed" ? "secondary" : "outline"} className="text-[10px]">{t.status}</Badge>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <Badge variant="outline" className="text-[10px] capitalize">{t.type}</Badge>
                    <p className="text-[11px] text-gray-500 truncate">{t.description}</p>
                  </div>
                  {(t.attachment_urls || []).length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      {t.attachment_urls.slice(0, 3).map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noreferrer" className="w-10 h-10 rounded border overflow-hidden block">
                          <img src={u} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
